import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

import styles from './styles.module.scss';
import BounceLoader from 'react-spinners/BounceLoader';
import errorMsg from '../../../../Functions/errorMsg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faBoxes, faBoxOpen, faCartArrowDown, faCartShopping, faCashRegister, faFileArrowUp, faFileFragment, faFileHalfDashed, faMoneyBillTransfer, faMoneyBillWave, faMoneyCheck, faMoneyCheckDollar, faRoadCircleCheck, faShop, faShoppingBag, faStar, faToolbox, faUserGroup } from '@fortawesome/free-solid-svg-icons';

export default function Overview() {
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const overviewRef = useRef(null);

  useEffect(() => {
    if (overviewRef.current) {
        overviewRef.current.scrollTop = overviewRef.current.scrollHeight; // Scroll to top for reversed columns
    }
  }, []);


  useEffect(() => {
    if (overviewData || loading || hasError) return;

    const fetchOverviewData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${window.location.origin}/api/vendor/manage/overview/getOverviewData`);
        setOverviewData(response.data);
      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
        setHasError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchOverviewData();
  }, [overviewData, loading, hasError]);

  if (loading || hasError || !overviewData) {
    return (
      <section id={styles.loading}>
        <BounceLoader size={75} speedMultiplier={2} color="#154734" />
      </section>
    );
  }

  const {
    ordersAwaitingCompletion,
    totalReviews,
    averageRating,
    productSalesTotal,
    completedServiceRequests,
    ordersAwaitingPayment,
    uniqueClients,
    totalOrders,
    totalItemsSold,
    abandonedCartsTotal,
    activeCartsTotal,
    ordersPercentageChange,
    profitPercentageChange,
    completedServicesPercentageChange,
    stockSoldPercentageChange,
    recentOrders,
  } = overviewData;

  return (
    <section ref={overviewRef} id={styles.wrapper}>

      <div id={styles.sideData}>

        <section className={styles.sideDataCard}>
          <FontAwesomeIcon icon={faCashRegister} id={styles.icon} />
          <div>
            <h1>Verified Profits (30 Days)</h1>
            <h2>
              {Math.abs(profitPercentageChange)}%{' '}
              {profitPercentageChange !== 0 && (profitPercentageChange >= 0 ? (
                <FontAwesomeIcon icon={faArrowUp} id={styles.iconUp} />
              ) : (
                <FontAwesomeIcon icon={faArrowDown} id={styles.iconDown} />
              ))}
            </h2>
          </div>
        </section>

        <hr />

        <section className={styles.sideDataCard}>
          <FontAwesomeIcon icon={faShop} id={styles.icon} />
          <div>
            <h1>Orders (30 Days)</h1>
            <h2>
              {Math.abs(ordersPercentageChange)}%{' '}
              {ordersPercentageChange !== 0 && (ordersPercentageChange >= 0 ? (
                <FontAwesomeIcon icon={faArrowUp} id={styles.iconUp} />
              ) : (
                <FontAwesomeIcon icon={faArrowDown} id={styles.iconDown} />
              ))}
            </h2>
          </div>
        </section>

        <hr />

        <section className={styles.sideDataCard}>
          <FontAwesomeIcon icon={faBoxes} id={styles.icon} />
          <div>
            <h1>Stock Sold (30 Days)</h1>
            <h2>
              {Math.abs(stockSoldPercentageChange)}%{' '}
              {stockSoldPercentageChange !== 0 && (stockSoldPercentageChange >= 0 ? (
                <FontAwesomeIcon icon={faArrowUp} id={styles.iconUp} />
              ) : (
                <FontAwesomeIcon icon={faArrowDown} id={styles.iconDown} />
              ))}
            </h2>
          </div>
        </section>

        <hr />

        <section className={styles.sideDataCard}>
          <FontAwesomeIcon icon={faToolbox} id={styles.icon} />
          <div>
            <h1>Services Completed (30 Days)</h1>
            <h2>
              {Math.abs(completedServicesPercentageChange)}%{' '}
              {completedServicesPercentageChange !== 0 && (completedServicesPercentageChange >= 0 ? (
                <FontAwesomeIcon icon={faArrowUp} id={styles.iconUp} />
              ) : (
                <FontAwesomeIcon icon={faArrowDown} id={styles.iconDown} />
              ))}
            </h2>
          </div>
        </section>

      </div>

      <div id={styles.summaryData}>
        <h1>Shop Overview</h1>

          <section id={styles.smallDataContainer}>

              <div className={styles.smallDataCard}>
                <FontAwesomeIcon icon={faStar} id={styles.icon} />
                <div>
                  <h1>Shop Rating ({totalReviews})</h1>
                  <h2>{averageRating}</h2>
                </div>
              </div>

              <div className={styles.smallDataCard}>
                <FontAwesomeIcon icon={faShoppingBag} id={styles.icon} />
                <div>
                  <h1>Product Sales</h1>
                  <h2>${productSalesTotal}</h2>
                </div>
              </div>

              <div className={styles.smallDataCard}>
                <FontAwesomeIcon icon={faBoxOpen} id={styles.icon} />
                <div>
                  <h1>Stock Sold</h1>
                  <h2>{totalItemsSold}</h2>
                </div>
              </div>

              <div className={styles.smallDataCard}>
                <FontAwesomeIcon icon={faFileArrowUp} id={styles.icon} />
                <div>
                  <h1>Total Orders</h1>
                  <h2>{totalOrders}</h2>
                </div>
              </div>

              <div className={styles.smallDataCard}>
                <FontAwesomeIcon icon={faUserGroup} id={styles.icon} />
                <div>
                  <h1>Unique Clients</h1>
                  <h2>{uniqueClients}</h2>
                </div>
              </div>

              <div className={styles.smallDataCard}>
                <FontAwesomeIcon icon={faRoadCircleCheck} id={styles.icon} />
                <div>
                  <h1>Completed Services</h1>
                  <h2>{completedServiceRequests}</h2>
                </div>
              </div>

              <div className={styles.smallDataCard}>
                <FontAwesomeIcon icon={faMoneyBillTransfer} id={styles.icon} />
                <div>
                  <h1>Awaiting Payment</h1>
                  <h2>{ordersAwaitingPayment}</h2>
                </div>
              </div>

              <div className={styles.smallDataCard}>
                <FontAwesomeIcon icon={faFileFragment} id={styles.icon} />
                <div>
                  <h1>Incomplete Orders</h1>
                  <h2>{ordersAwaitingCompletion}</h2>
                </div>
              </div>

              <div className={styles.smallDataCard}>
                <FontAwesomeIcon icon={faCartShopping} id={styles.icon} />
                <div>
                  <h1>Active Carts</h1>
                  <h2>{activeCartsTotal}</h2>
                </div>
              </div>

              <div className={styles.smallDataCard}>
                <FontAwesomeIcon icon={faCartArrowDown} id={styles.icon} />
                <div>
                  <h1>Abandoned Carts</h1>
                  <h2>{abandonedCartsTotal}</h2>
                </div>
              </div>

          </section>

          <section id={styles.recentOrders}>
            <h1>Recent Orders</h1>
            { recentOrders.length === 0 && <section><h1 id={styles.noOrders}>No Recent Orders</h1></section> }
            { recentOrders.length > 0 && <section>
              {
              recentOrders.map((order, index) => {
                return (
                  <div className={styles.recentOrderCard} key={index}>
                    <section>
                      <h1>{order.name}</h1>
                      <h2>{order.price}</h2>
                    </section>
                    <h1>{order.created}</h1>
                  </div>
                )
              })
              }
            </section>
            }

          </section>

      </div>
    </section>
  );
}
